/**
 * @fileoverview ClubOS Youth Academy — Players Management
 * MSC-YYYY-XXXX player codes, batch assignment, status tracking, detail view.
 * Changes: Scholarship 25%, admission fee tab (one-time, admin-unlock), PDF download,
 *          date-slider + status filters, full mandatory validation.
 */

import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { academyService } from "api";
import { useAuth } from "context/AuthContext";
import FootballLoader from "components/FootballLoader";

const STATUS_COLOR = { Active: "success", Inactive: "secondary", Trial: "warning" };
const FEE_PLAN_COLOR = { Standard: "info", "Scholarship (25%)": "warning", "Full Waiver": "success" };
const SEVERITY_COLOR = { Minor: "success", Moderate: "warning", Severe: "error" };
const ATTENDANCE_COLOR = { Present: "success", Late: "warning", Absent: "error", Leave: "info" };

const DEFAULT_FEE_PLANS = [
  { value: "Standard", label: "Standard", effectiveMonthlyFee: 0, effectiveAdmissionFee: 0, subsidyPct: 0 },
];

const CURRENT_YEAR = new Date().getFullYear();

const emptyForm = {
  fullName: "", dateOfBirth: "", parentName: "", parentPhone: "", alternatePhone: "",
  parentEmail: "", address: "", monthlyFee: "", feePlan: "", batchId: "", status: "Active", notes: "",
};

const ADMISSION_ITEM_TYPES = ["Admission", "Jersey", "Shorts", "Socks", "Backpack", "Cap", "Donation"];

const makeDefaultItems = () =>
  ADMISSION_ITEM_TYPES.map((type) => ({ itemType: type, amount: "", quantity: 1, isPaid: false, notes: "" }));

const emptyAdmissionForm = {
  admissionDate: new Date().toISOString().slice(0, 10),
};

// ── Validate player form ────────────────────────────────────────────────────────
function validatePlayerForm(form, admissionItems, admissionDate, isNew) {
  const errs = {};
  if (!form.fullName.trim()) errs.fullName = "Full name is required.";
  if (!form.dateOfBirth) errs.dateOfBirth = "Date of birth is required.";
  if (!form.parentName.trim()) errs.parentName = "Parent/Guardian name is required.";
  if (!form.parentPhone.trim()) errs.parentPhone = "Parent phone is required.";
  else if (!/^\d{10}$/.test(form.parentPhone.replace(/\s/g, ""))) errs.parentPhone = "Enter a valid 10-digit phone number.";
  if (form.alternatePhone && !/^\d{10}$/.test(form.alternatePhone.replace(/\s/g, ""))) errs.alternatePhone = "Enter a valid 10-digit number.";
  if (form.parentEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.parentEmail)) errs.parentEmail = "Enter a valid email address.";
  if (!form.address.trim()) errs.address = "Address is required.";
  if (!form.monthlyFee || Number(form.monthlyFee) < 0) errs.monthlyFee = "Monthly fee is required.";
  if (!form.feePlan) errs.feePlan = "Fee plan is required.";
  if (!form.batchId) errs.batchId = "Batch assignment is required.";
  if (!admissionDate) errs.admissionDate = "Admission date is required.";
  // Block save if no admission items have amounts (at least one item must have amount > 0)
  const hasAnyAmount = admissionItems.some((it) => Number(it.amount) > 0);
  if (!hasAnyAmount) errs.admissionItems = "Please fill in at least one admission fee amount before saving.";
  return errs;
}

// ── Generate PDF report ─────────────────────────────────────────────────────────
async function downloadPlayersPDF(players, filterStatus, filterBatch, batches, yearRange, search) {
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const batchName = batches.find((b) => b.id === filterBatch)?.name ?? "All Batches";
  const now = new Date().toLocaleString("en-IN");

  doc.setFontSize(16);
  doc.text("Academy Players Report", 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${now}`, 14, 25);
  doc.text(`Filters — Status: ${filterStatus || "All"} | Batch: ${batchName} | Search: ${search || "None"}`, 14, 30);

  autoTable(doc, {
    startY: 36,
    head: [["#", "Player Code", "Full Name", "DOB", "Age Grp", "Batch", "Fee/Mo", "Plan", "Parent", "Phone", "Status"]],
    body: players.map((p, i) => [
      i + 1,
      p.playerCode,
      p.fullName,
      p.dateOfBirth ? String(p.dateOfBirth).slice(0, 10) : "",
      p.ageGroup,
      p.batchName ?? "Unassigned",
      `₹${Number(p.monthlyFee).toLocaleString("en-IN")}`,
      p.feePlan ?? "Standard",
      p.parentName ?? "",
      p.parentPhone ?? "",
      p.status,
    ]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [26, 115, 232], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 248, 255] },
  });

  doc.save(`players-report-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ── Player row ─────────────────────────────────────────────────────────────────
function PlayerRow({ player, onEdit, onView }) {
  return (
    <SoftBox
      display="flex" alignItems="center" py={1} px={2}
      sx={{
        borderBottom: "1px solid #f5f5f5",
        cursor: "pointer",
        minWidth: 700,
        "&:hover": { backgroundColor: "rgba(26,115,232,0.03)" },
      }}
      onClick={() => onView(player)}
    >
      <SoftBox flex="0 0 115px">
        <SoftTypography variant="caption" fontWeight="bold" color="info" sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
          {player.playerCode}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="1" display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
        <SoftTypography variant="button" fontWeight="medium" color="dark" sx={{ whiteSpace: "nowrap" }}>
          {player.fullName}
        </SoftTypography>
        <SoftTypography variant="caption" color="text" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
          · {player.ageGroup} · Age {player.age} · {player.batchName ?? "Unassigned"}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 80px" textAlign="center">
        <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ whiteSpace: "nowrap" }}>
          ₹{Number(player.monthlyFee).toLocaleString("en-IN")}
        </SoftTypography>
      </SoftBox>
      <SoftBox flex="0 0 130px" textAlign="center">
        <SoftBadge
          badgeContent={player.feePlan ?? "Standard"}
          color={FEE_PLAN_COLOR[player.feePlan] ?? "info"}
          size="xs" container
        />
      </SoftBox>
      <SoftBox flex="0 0 80px" textAlign="center">
        <SoftBadge badgeContent={player.status} color={STATUS_COLOR[player.status] ?? "info"} size="xs" container />
      </SoftBox>
      <SoftBox flex="0 0 40px" textAlign="right">
        <SoftButton variant="text" color="info" iconOnly size="small"
          onClick={(e) => { e.stopPropagation(); onEdit(player); }}>
          <Icon>edit</Icon>
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}

PlayerRow.propTypes = {
  player: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
};

// ── Main component ─────────────────────────────────────────────────────────────
function AcademyPlayers() {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === "Admin" || currentUser?.role === "SuperAdmin";

  const [players, setPlayers]           = useState([]);
  const [batches, setBatches]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterBatch, setFilterBatch]   = useState("");
  const [yearRange, setYearRange]       = useState([CURRENT_YEAR - 5, CURRENT_YEAR]);

  const [feePlans, setFeePlans]             = useState(DEFAULT_FEE_PLANS);

  const [dialogOpen, setDialogOpen]         = useState(false);
  const [dialogTab, setDialogTab]           = useState(0);   // 0=Profile 1=AdmissionItems
  const [editing, setEditing]               = useState(null);
  const [form, setForm]                     = useState(emptyForm);
  const [admissionDate, setAdmissionDate]   = useState(new Date().toISOString().slice(0, 10));
  const [admissionItems, setAdmissionItems] = useState(makeDefaultItems());
  const [admissionLocked, setAdmissionLocked] = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [fieldErrors, setFieldErrors]       = useState({});
  const [saveError, setSaveError]           = useState("");

  // Detail view
  const [viewPlayer, setViewPlayer]       = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [attendance, setAttendance]       = useState([]);
  const [injuries, setInjuries]           = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      academyService.getPlayers({
        status: filterStatus || undefined,
        batchId: filterBatch || undefined,
        search: search || undefined,
      }),
      academyService.getBatches({ status: "Active" }),
      academyService.getFeePlans().catch(() => null),
    ])
      .then(([p, b, plans]) => {
        setPlayers(Array.isArray(p) ? p : []);
        setBatches(Array.isArray(b) ? b : []);
        if (Array.isArray(plans) && plans.length > 0) setFeePlans(plans);
      })
      .catch((err) => {
        console.error("Failed to load players:", err);
        setPlayers([]);
        setBatches([]);
      })
      .finally(() => setLoading(false));
  }, [filterStatus, filterBatch, search]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, feePlan: feePlans[0]?.name ?? "" });
    setAdmissionDate(new Date().toISOString().slice(0, 10));
    setAdmissionItems(makeDefaultItems());
    setAdmissionLocked(false);
    setFieldErrors({});
    setSaveError("");
    setDialogTab(0);
    setDialogOpen(true);
  };

  const openEdit = async (player) => {
    setEditing(player);
    setForm({
      fullName: player.fullName,
      dateOfBirth: player.dateOfBirth ? String(player.dateOfBirth).slice(0, 10) : "",
      parentName: player.parentName ?? "",
      parentPhone: player.parentPhone ?? "",
      alternatePhone: player.alternatePhone ?? "",
      parentEmail: player.parentEmail ?? "",
      address: player.address ?? "",
      monthlyFee: player.monthlyFee,
      feePlan: player.feePlan ?? (feePlans[0]?.name ?? ""),
      batchId: player.batchId ?? "",
      status: player.status ?? "Active",
      notes: player.notes ?? "",
    });
    setAdmissionDate(player.admissionDate ? String(player.admissionDate).slice(0, 10) : new Date().toISOString().slice(0, 10));
    // Load existing admission items from API
    const defaults = makeDefaultItems();
    try {
      const existing = await academyService.getAdmissionItems(player.id);
      if (Array.isArray(existing) && existing.length > 0) {
        const merged = defaults.map((d) => {
          const found = existing.find((e) => e.itemType === d.itemType);
          return found ? { itemType: found.itemType, amount: String(found.amount), quantity: found.quantity, isPaid: found.isPaid, notes: found.notes ?? "" } : d;
        });
        setAdmissionItems(merged);
        setAdmissionLocked(true);
      } else {
        setAdmissionItems(defaults);
        setAdmissionLocked(false);
      }
    } catch {
      setAdmissionItems(defaults);
      setAdmissionLocked(false);
    }
    setFieldErrors({});
    setSaveError("");
    setDialogTab(0);
    setDialogOpen(true);
  };

  const openView = async (player) => {
    setViewPlayer(player);
    setDetailLoading(true);
    setAttendance([]);
    setInjuries([]);
    try {
      const [att, inj] = await Promise.all([
        academyService.getAttendance({ playerId: player.id }),
        academyService.getInjuries({ playerId: player.id }),
      ]);
      setAttendance(Array.isArray(att) ? att.slice(0, 10) : []);
      setInjuries(Array.isArray(inj) ? inj : []);
    } catch (e) {
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const f = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const updateAdmissionItem = (index, field, value) => {
    setAdmissionItems((prev) => prev.map((it, i) => i === index ? { ...it, [field]: value } : it));
    setFieldErrors((prev) => ({ ...prev, admissionItems: undefined }));
  };

  // When a fee plan is selected, auto-populate monthly fee and Admission line item from plan
  const handleFeePlanSelect = (planName) => {
    const plan = feePlans.find((p) => p.name === planName);
    setForm((prev) => ({
      ...prev,
      feePlan: planName,
      monthlyFee: plan ? String(plan.effectiveMonthlyFee) : prev.monthlyFee,
    }));
    if (plan && plan.effectiveAdmissionFee > 0) {
      setAdmissionItems((prev) => prev.map((it) =>
        it.itemType === "Admission" ? { ...it, amount: String(plan.effectiveAdmissionFee) } : it
      ));
    }
    setFieldErrors((prev) => ({ ...prev, feePlan: undefined, monthlyFee: undefined }));
  };

  const handleSave = async () => {
    const errs = validatePlayerForm(form, admissionItems, admissionDate, !editing);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setSaveError("Please fix the highlighted errors before saving.");
      if (errs.admissionItems || errs.admissionDate) setDialogTab(1);
      else setDialogTab(0);
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        ...form,
        monthlyFee: Number(form.monthlyFee) || 0,
        batchId: form.batchId || null,
        alternatePhone: form.alternatePhone || null,
        dateOfBirth: form.dateOfBirth,
        admissionDate: admissionDate || new Date().toISOString().slice(0, 10),
      };
      let playerId;
      if (editing) {
        await academyService.updatePlayer(editing.id, payload);
        playerId = editing.id;
      } else {
        playerId = await academyService.createPlayer(payload);
      }
      // Save admission items (only items that have an amount)
      const itemsToSave = admissionItems
        .filter((it) => Number(it.amount) > 0)
        .map((it) => ({
          itemType: it.itemType,
          amount: Number(it.amount),
          isPaid: it.isPaid,
          quantity: Number(it.quantity) || 1,
          notes: it.notes || null,
        }));
      if (itemsToSave.length > 0) {
        await academyService.saveAdmissionItems(playerId, itemsToSave);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail ?? e?.response?.data?.title ?? e?.message ?? "Save failed. Please try again.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleFeePlanChange = async (player, newPlan) => {
    await academyService.updatePlayer(player.id, { ...player, feePlan: newPlan });
    load();
  };

  const handleDownloadPDF = () => {
    downloadPlayersPDF(players, filterStatus, filterBatch, batches, yearRange, search);
  };

  const active   = players.filter((p) => p.status === "Active").length;
  const trial    = players.filter((p) => p.status === "Trial").length;
  const inactive = players.filter((p) => p.status === "Inactive").length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Academy Players</SoftTypography>
            <SoftTypography variant="body2" color="text">Click a player to view attendance &amp; injury history</SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1}>
            <SoftButton variant="outlined" color="info" startIcon={<Icon>download</Icon>} onClick={handleDownloadPDF}>
              Download Report
            </SoftButton>
            <SoftButton variant="gradient" color="info" startIcon={<Icon>person_add</Icon>} onClick={openAdd}>
              Add Player
            </SoftButton>
          </SoftBox>
        </SoftBox>

        {/* KPI row */}
        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Total Players" }} count={String(players.length)}
                percentage={{ color: "info", text: "registered" }} icon={{ color: "info", component: "groups" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Active" }} count={String(active)}
                percentage={{ color: "success", text: "training" }} icon={{ color: "success", component: "check_circle" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "On Trial" }} count={String(trial)}
                percentage={{ color: "warning", text: "evaluation" }} icon={{ color: "warning", component: "person_search" }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MiniStatisticsCard title={{ text: "Inactive" }} count={String(inactive)}
                percentage={{ color: "secondary", text: "inactive" }} icon={{ color: "secondary", component: "person_off" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Filters */}
        <Card>
          <SoftBox p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField
              size="small" placeholder="Search by name, code, or parent…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Icon fontSize="small">search</Icon></InputAdornment> }}
              sx={{ minWidth: 260 }}
            />
            <TextField select size="small" label="Status" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 130 }}>
              <MenuItem value="">◈ All Status</MenuItem>
              <MenuItem value="Active">▲ Active</MenuItem>
              <MenuItem value="Trial">▶ Trial</MenuItem>
              <MenuItem value="Inactive">▼ Inactive</MenuItem>
            </TextField>
            <TextField select size="small" label="Batch" value={filterBatch}
              onChange={(e) => setFilterBatch(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">All Batches</MenuItem>
              {batches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </TextField>

            {/* Year range inputs */}
            <TextField select size="small" label="Year From" value={yearRange[0]}
              onChange={(e) => setYearRange([Number(e.target.value), yearRange[1]])} sx={{ minWidth: 110 }}>
              {Array.from({ length: CURRENT_YEAR - 2014 }, (_, i) => 2015 + i).map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Year To" value={yearRange[1]}
              onChange={(e) => setYearRange([yearRange[0], Number(e.target.value)])} sx={{ minWidth: 110 }}>
              {Array.from({ length: CURRENT_YEAR - 2014 }, (_, i) => 2015 + i).map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>

            {(filterStatus || filterBatch || search) && (
              <Chip
                label="Clear filters"
                size="small"
                onDelete={() => { setFilterStatus(""); setFilterBatch(""); setSearch(""); }}
                sx={{ height: 28 }}
              />
            )}
            <SoftTypography variant="caption" color="text" ml="auto">
              {players.length} players found
            </SoftTypography>
          </SoftBox>

          {/* Scrollable table area */}
          <SoftBox sx={{ overflowX: "auto" }}>
            {/* Column headers */}
            <SoftBox display="flex" alignItems="center" px={2} py={1}
              sx={{ borderBottom: "2px solid #f0f0f0", backgroundColor: "#fafafa", minWidth: 700 }}>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 115px" }}>ID CODE</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>PLAYER</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>FEE/MO</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 130px", textAlign: "center" }}>PLAN</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>STATUS</SoftTypography>
              <SoftBox sx={{ flex: "0 0 40px" }} />
            </SoftBox>

            {loading ? (
              <FootballLoader label="Loading players…" />
            ) : players.length === 0 ? (
              <SoftBox p={4} textAlign="center">
                <SoftTypography variant="caption" color="text">No players found</SoftTypography>
              </SoftBox>
            ) : (
              players.map((p) => (
                <PlayerRow key={p.id} player={p} onEdit={openEdit} onView={openView} />
              ))
            )}
          </SoftBox>
        </Card>

      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />

      {/* ── Add / Edit dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Edit Player" : "Add Academy Player"}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {saveError && <Alert severity="error" sx={{ mx: 3, mt: 2 }}>{saveError}</Alert>}

          <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ px: 3, borderBottom: "1px solid #f0f0f0" }}>
            <Tab label="Details" />
            <Tab label="Fees" />
          </Tabs>

          {/* ── Profile Tab ── */}
          {dialogTab === 0 && (
            <Grid container spacing={2} sx={{ p: 3, pt: 2 }}>
              <Grid item xs={12}>
                <TextField fullWidth required label="Full Name" value={form.fullName}
                  onChange={f("fullName")} size="small"
                  error={Boolean(fieldErrors.fullName)} helperText={fieldErrors.fullName} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth required label="Date of Birth" type="date" value={form.dateOfBirth}
                  onChange={f("dateOfBirth")} size="small" InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldErrors.dateOfBirth)} helperText={fieldErrors.dateOfBirth} />
              </Grid>
              <Grid item xs={6}>
                <TextField select fullWidth required label="Fee Plan" value={form.feePlan}
                  onChange={(e) => handleFeePlanSelect(e.target.value)} size="small"
                  error={Boolean(fieldErrors.feePlan)} helperText={fieldErrors.feePlan}>
                  {feePlans.map((p) => (
                    <MenuItem key={p.id ?? p.name} value={p.name}>
                      {p.name}
                      {p.subsidyPct > 0 ? ` (${p.subsidyPct}% off)` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {/* Non-editable base fee info from the selected plan */}
              {form.feePlan && (() => {
                const plan = feePlans.find((p) => p.name === form.feePlan);
                if (!plan) return null;
                return (
                  <Grid item xs={12}>
                    <SoftBox sx={{ backgroundColor: "#f0f7ff", border: "1px solid #90caf9", borderRadius: 2, px: 2, py: 1.25 }}>
                      <SoftTypography variant="caption" fontWeight="bold" color="info" display="block" mb={0.75}>
                        Plan: {plan.name}{plan.subsidyPct > 0 ? ` — ${plan.subsidyPct}% subsidy applied` : ""}
                      </SoftTypography>
                      <SoftBox display="flex" gap={3} flexWrap="wrap">
                        <SoftBox>
                          <SoftTypography variant="caption" color="text">Base Monthly</SoftTypography>
                          <SoftTypography variant="button" fontWeight="bold" color="dark" display="block">
                            ₹{Number(plan.baseMonthlyFee).toLocaleString("en-IN")}
                          </SoftTypography>
                        </SoftBox>
                        {plan.subsidyPct > 0 && (
                          <SoftBox>
                            <SoftTypography variant="caption" color="text">Effective Monthly</SoftTypography>
                            <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#2e7d32" }} display="block">
                              ₹{Number(plan.effectiveMonthlyFee).toLocaleString("en-IN")}
                            </SoftTypography>
                          </SoftBox>
                        )}
                        <SoftBox>
                          <SoftTypography variant="caption" color="text">Base Admission</SoftTypography>
                          <SoftTypography variant="button" fontWeight="bold" color="dark" display="block">
                            ₹{Number(plan.baseAdmissionFee).toLocaleString("en-IN")}
                          </SoftTypography>
                        </SoftBox>
                        {plan.subsidyPct > 0 && (
                          <SoftBox>
                            <SoftTypography variant="caption" color="text">Effective Admission</SoftTypography>
                            <SoftTypography variant="button" fontWeight="bold" sx={{ color: "#2e7d32" }} display="block">
                              ₹{Number(plan.effectiveAdmissionFee).toLocaleString("en-IN")}
                            </SoftTypography>
                          </SoftBox>
                        )}
                      </SoftBox>
                    </SoftBox>
                  </Grid>
                );
              })()}
              <Grid item xs={6}>
                <TextField fullWidth required label="Monthly Fee (₹)" type="number" value={form.monthlyFee}
                  onChange={f("monthlyFee")} size="small"
                  error={Boolean(fieldErrors.monthlyFee)} helperText={fieldErrors.monthlyFee}
                  InputProps={{
                    readOnly: feePlans.some((p) => p.name === form.feePlan && p.subsidyPct === 100),
                  }} />
              </Grid>
              <Grid item xs={6}>
                <TextField select fullWidth required label="Batch" value={form.batchId}
                  onChange={f("batchId")} size="small"
                  error={Boolean(fieldErrors.batchId)} helperText={fieldErrors.batchId}>
                  <MenuItem value="">Unassigned</MenuItem>
                  {batches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name} ({b.ageGroup})</MenuItem>)}
                </TextField>
              </Grid>
              {editing && (
                <Grid item xs={6}>
                  <TextField select fullWidth label="Status" value={form.status}
                    onChange={f("status")} size="small">
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Trial">Trial</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </TextField>
                </Grid>
              )}
              <Grid item xs={editing ? 6 : 12}>
                <TextField fullWidth required label="Parent / Guardian Name" value={form.parentName}
                  onChange={f("parentName")} size="small"
                  error={Boolean(fieldErrors.parentName)} helperText={fieldErrors.parentName} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth required label="Parent Phone" value={form.parentPhone}
                  onChange={f("parentPhone")} size="small"
                  error={Boolean(fieldErrors.parentPhone)} helperText={fieldErrors.parentPhone} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Alternate Phone" value={form.alternatePhone}
                  onChange={f("alternatePhone")} size="small"
                  error={Boolean(fieldErrors.alternatePhone)} helperText={fieldErrors.alternatePhone} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Parent Email" value={form.parentEmail}
                  onChange={f("parentEmail")} size="small"
                  error={Boolean(fieldErrors.parentEmail)} helperText={fieldErrors.parentEmail} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth required label="Address" value={form.address}
                  onChange={f("address")} size="small" multiline rows={2}
                  error={Boolean(fieldErrors.address)} helperText={fieldErrors.address} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Notes" value={form.notes}
                  onChange={f("notes")} size="small" multiline rows={2} />
              </Grid>
            </Grid>
          )}

          {/* ── Admission Items Tab ── */}
          {dialogTab === 1 && (
            <SoftBox p={3}>
              {fieldErrors.admissionItems && (
                <Alert severity="error" sx={{ mb: 2 }}>{fieldErrors.admissionItems}</Alert>
              )}
              <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <SoftBox display="flex" alignItems="center" gap={2}>
                  <TextField
                    label="Admission Date" type="date" size="small"
                    value={admissionDate}
                    onChange={(e) => { setAdmissionDate(e.target.value); setFieldErrors((p) => ({ ...p, admissionDate: undefined })); }}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldErrors.admissionDate)}
                    helperText={fieldErrors.admissionDate}
                    sx={{ minWidth: 170 }}
                  />
                  {admissionLocked && isAdmin && (
                    <SoftButton size="small" color="warning" variant="outlined"
                      onClick={() => setAdmissionLocked(false)}>
                      <Icon fontSize="small" sx={{ mr: 0.5 }}>lock_open</Icon> Unlock to Edit
                    </SoftButton>
                  )}
                </SoftBox>
                <SoftTypography variant="caption" color="text">
                  Total: ₹{admissionItems.reduce((s, it) => s + (Number(it.amount) || 0) * (Number(it.quantity) || 1), 0).toLocaleString("en-IN")}
                  &nbsp;·&nbsp;
                  Paid: ₹{admissionItems.filter((it) => it.isPaid).reduce((s, it) => s + (Number(it.amount) || 0) * (Number(it.quantity) || 1), 0).toLocaleString("en-IN")}
                </SoftTypography>
              </SoftBox>

              {/* Items header */}
              <SoftBox display="flex" alignItems="center" px={1} py={0.5} mb={0.5}
                sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 110px" }}>ITEM</SoftTypography>
                <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "1" }}>AMOUNT (₹)</SoftTypography>
                <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 70px", textAlign: "center" }}>QTY</SoftTypography>
                <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 60px", textAlign: "center" }}>PAID</SoftTypography>
              </SoftBox>

              {admissionItems.map((item, idx) => (
                <SoftBox key={item.itemType} display="flex" alignItems="center" gap={1} px={1} py={0.75}
                  sx={{ borderBottom: "1px solid #f5f5f5", opacity: admissionLocked ? 0.7 : 1 }}>
                  <SoftTypography variant="caption" fontWeight="medium" color="dark" sx={{ flex: "0 0 110px" }}>
                    {item.itemType}
                  </SoftTypography>
                  <TextField
                    type="number" size="small" placeholder="0"
                    value={item.amount}
                    onChange={(e) => updateAdmissionItem(idx, "amount", e.target.value)}
                    disabled={admissionLocked}
                    sx={{ flex: 1 }}
                    inputProps={{ min: 0, style: { textAlign: "right" } }}
                  />
                  <TextField
                    type="number" size="small"
                    value={item.quantity}
                    onChange={(e) => updateAdmissionItem(idx, "quantity", Math.max(1, Number(e.target.value)))}
                    disabled={admissionLocked}
                    sx={{ flex: "0 0 70px" }}
                    inputProps={{ min: 1 }}
                  />
                  <SoftBox flex="0 0 60px" textAlign="center">
                    <input
                      type="checkbox"
                      checked={item.isPaid}
                      disabled={admissionLocked}
                      onChange={(e) => updateAdmissionItem(idx, "isPaid", e.target.checked)}
                      style={{ width: 18, height: 18, cursor: admissionLocked ? "not-allowed" : "pointer" }}
                    />
                  </SoftBox>
                </SoftBox>
              ))}
            </SoftBox>
          )}
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => setDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editing ? "Update" : "Add Player"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Player detail dialog ── */}
      <Dialog open={Boolean(viewPlayer)} onClose={() => setViewPlayer(null)} maxWidth="sm" fullWidth>
        {viewPlayer && (
          <>
            <DialogTitle>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                <SoftBox>
                  <SoftTypography variant="h6" fontWeight="bold">{viewPlayer.fullName}</SoftTypography>
                  <SoftTypography variant="caption" color="text" sx={{ fontFamily: "monospace" }}>
                    {viewPlayer.playerCode} · {viewPlayer.batchName ?? "Unassigned"}
                  </SoftTypography>
                </SoftBox>
                <SoftBadge badgeContent={viewPlayer.status} color={STATUS_COLOR[viewPlayer.status] ?? "info"} size="xs" container />
              </SoftBox>
            </DialogTitle>
            <DialogContent>
              {detailLoading ? (
                <SoftBox py={3} textAlign="center">
                  <SoftTypography variant="caption" color="text">Loading…</SoftTypography>
                </SoftBox>
              ) : (
                <>
                  {/* Active injuries */}
                  <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                    INJURIES
                  </SoftTypography>
                  {injuries.length === 0 ? (
                    <SoftTypography variant="caption" color="text">No injury records.</SoftTypography>
                  ) : (
                    injuries.map((inj) => (
                      <SoftBox key={inj.id} display="flex" justifyContent="space-between" alignItems="center"
                        py={1} sx={{ borderBottom: "1px solid #f5f5f5" }}>
                        <SoftBox>
                          <SoftTypography variant="caption" fontWeight="medium" color="dark">
                            {inj.injuryType} — {inj.bodyPart}
                          </SoftTypography>
                          <SoftTypography variant="caption" color="text" display="block">
                            {String(inj.injuryDate)} · {inj.physioNotes ?? ""}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox display="flex" gap={0.5}>
                          <SoftBadge badgeContent={inj.severity} color={SEVERITY_COLOR[inj.severity] ?? "info"} size="xs" container />
                          <SoftBadge badgeContent={inj.recoveryStatus}
                            color={inj.recoveryStatus === "Cleared" ? "success" : inj.recoveryStatus === "Recovering" ? "warning" : "error"}
                            size="xs" container />
                        </SoftBox>
                      </SoftBox>
                    ))
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Last 10 attendance */}
                  <SoftTypography variant="caption" fontWeight="bold" color="text" display="block" mb={1}>
                    RECENT ATTENDANCE (last 10)
                  </SoftTypography>
                  {attendance.length === 0 ? (
                    <SoftTypography variant="caption" color="text">No attendance records.</SoftTypography>
                  ) : (
                    attendance.map((a) => (
                      <SoftBox key={a.id} display="flex" justifyContent="space-between" alignItems="center"
                        py={0.75} sx={{ borderBottom: "1px solid #f5f5f5" }}>
                        <SoftTypography variant="caption" color="text">
                          {String(a.attendanceDate)} · {a.batchName}
                        </SoftTypography>
                        <SoftBadge badgeContent={a.attendanceStatus} color={ATTENDANCE_COLOR[a.attendanceStatus] ?? "info"} size="xs" container />
                      </SoftBox>
                    ))
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <SoftButton variant="text" color="secondary" onClick={() => setViewPlayer(null)}>Close</SoftButton>
              <SoftButton variant="gradient" color="info" onClick={() => { setViewPlayer(null); openEdit(viewPlayer); }}>
                Edit Player
              </SoftButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </DashboardLayout>
  );
}

export default AcademyPlayers;
